<template>
  <div>
    <vue-data-table
      :headers="headers"
      :data="stockItems"
      :items-per-page="10"
      :buttons="buttons"
      :summable-columns="summableColumns"
      :show-filter="true"
      :filter-fields="filterFields"
      :filter-data="filter"
      :filter-title="'Filtrer les stocks'"
      :table-actions="tableActions"
      @filter="onFilter"
      @reset="onReset"
    >
      <template #cell-quantity="{ row }">
        <span
          class="p-2 rounded-2"
          :class="
            parseFloat(row.quantity || 0) == 0
              ? 'bg-danger text-white'
              : parseFloat(row.quantity || 0) <= 5
              ? 'bg-warning'
              : 'text-success'
          "
        >
          <strong>{{ row.quantity }}</strong>
          {{ row.measuring }}
        </span>
      </template>

      <template #cell-stockDepot="{ row }">
        {{ row.stockFullName }} / {{ row.depositFullName }}
      </template>

      <template #cell-purchasePriceTTC="{ row }">
        <span v-if="userRole == 'Admin'">
          {{ row.purchasePriceTTC }}
        </span>
        <span v-else>-</span>
      </template>

      <template #cell-coldRoom="{ row }">
        <span v-if="row.cold_room">
          {{ row.cold_room.number }}
        </span>
        <span v-else>-</span>
      </template>
    </vue-data-table>
  </div>
</template>


<script>
import { mapGetters } from "vuex";
export default {
  components: {},
  data() {
    return {
      filter: {
        deposit_reference: "",
        stock_reference: "",
        section_reference: "",
        item_reference: "",
        startDate: "",
        endDate: "",
      },
      headers: [
        { field: "item_reference", label: "Référence", class: "" },
        { field: "itemFullName", label: "Désignation", class: "" },
        { field: "sectionFullName", label: "Catégorie", class: "" },
        { field: "stockDepot", label: "Stock / Dépôt", class: "" },
        { field: "quantity", label: "Quantité", class: "" },
        { field: "purchasePriceTTC", label: "Prix d'achat", class: "" },
        { field: "coldRoom", label: "Nº de Chambre", class: "" },
      ],
      buttons: [
        {
          label: "Modifier",
          icon: "bi bi-pencil-square",
          class: "btn btn-outline-primary",
          action: (row) => {
            if (row.reference) {
              this.$router.push({
                name: "stocks-edit",
                params: { reference: row.reference },
              });
            }
          },
          condition: () => this.userRole === "Admin",
        },
        {
          label: "Supprimer",
          icon: "bi bi-file-x-fill",
          class: "btn btn-outline-danger",
          action: (row) => {
            if (row.reference) {
              this.$router.push({
                name: "stocks-delete",
                params: { reference: row.reference },
              });
            }
          },
          condition: () => this.userRole === "Admin",
        },
      ],
      summableColumns: [
        { field: "quantity", class: "fw-bold" },
        { field: "purchasePriceTTC", class: "fw-bold" },
      ],
      filterFields: [
        {
          type: "select",
          model: "deposit_reference",
          label: "Dépôt",
          options: [],
          labelColumn: "fullName",
          valueColumn: "reference",
          placeholder: "Sélectionner un dépôt",
        },
        {
          type: "select",
          model: "stock_reference",
          label: "Désignation de stock",
          options: [],
          labelColumn: "fullName",
          valueColumn: "reference",
          placeholder: "Sélectionner un stock",
        },
        {
          type: "select",
          model: "section_reference",
          label: "Catégorie",
          options: [],
          labelColumn: "fullName",
          valueColumn: "reference",
          placeholder: "Sélectionner une catégorie",
        },
        {
          type: "select",
          model: "item_reference",
          label: "Produit",
          options: [],
          labelColumn: "fullName",
          valueColumn: "reference",
          placeholder: "Sélectionner un produit",
        },
      ],
      tableActions: [
        {
          title: "Actualiser",
          icon: "bi bi-arrow-clockwise",
          class: "btn btn-secondary",
          action: () => {
            this.refresh();
          },
        },
        {
          title: "Imprimer",
          icon: "bi bi-printer",
          class: "btn btn-secondary",
          action: () => {
            this.print();
          },
        },
        {
          title: "Envoyer",
          icon: "bi bi-envelope",
          class: "btn btn-secondary",
          action: () => {
            this.sendByEmail();
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters("stock", {
      stocks: "getStocks",
      stockItems: "getStockItems",
      stockFullNames: "getStockFullNames",
      totalQuantity: "getTotalQuantity",
      totalPurchasePriceTTC: "getTotalPurchasePriceTTC",
    }),
    ...mapGetters("item", {
      items: "getAll",
    }),
    ...mapGetters("section", {
      sections: "getAll",
    }),
    ...mapGetters("deposit", {
      deposits: "getAll",
    }),

    ...mapGetters("auth", {
      userRole: "getUserRole",
    }),
  },
  mounted() {
    this.$store.dispatch("stock/getAll");
    this.$store.dispatch("stock/fetchStocksItems");
    this.$store.dispatch("section/getAll");
    this.$store.dispatch("item/getAll");
    this.$store.dispatch("deposit/getAll");
  },
  watch: {
    deposits() {
      // Update filter field options when deposits are loaded
      const depositField = this.filterFields.find(
        (f) => f.model === "deposit_reference"
      );
      if (depositField) {
        depositField.options = this.deposits || [];
      }
    },
    stocks() {
      // Update filter field options when stocks are loaded
      const stockField = this.filterFields.find(
        (f) => f.model === "stock_reference"
      );
      if (stockField) {
        stockField.options = this.stocks || [];
      }
    },
    sections() {
      // Update filter field options when sections are loaded
      const sectionField = this.filterFields.find(
        (f) => f.model === "section_reference"
      );
      if (sectionField) {
        sectionField.options = this.sections || [];
      }
    },
    items() {
      // Update filter field options when items are loaded
      const itemField = this.filterFields.find(
        (f) => f.model === "item_reference"
      );
      if (itemField) {
        // Filter items by selected section if section is selected
        if (this.filter.section_reference) {
          itemField.options =
            this.items.filter(
              (i) => i.section_reference === this.filter.section_reference
            ) || [];
        } else {
          itemField.options = this.items || [];
        }
      }
    },
    "filter.section_reference"() {
      // Update item options when section changes
      const itemField = this.filterFields.find(
        (f) => f.model === "item_reference"
      );
      if (itemField) {
        if (this.filter.section_reference) {
          itemField.options = this.items.filter(
            (i) => i.section_reference === this.filter.section_reference
          );
        } else {
          itemField.options = this.items || [];
        }
      }
    },
  },
  methods: {
    async refresh() {
      await this.$store.dispatch("stock/getAll");
      await this.$store.dispatch("stock/fetchStocksItems");
      await this.$store.dispatch("section/getAll");
      await this.$store.dispatch("item/getAll");
      await this.$store.dispatch("deposit/getAll");

      // Reset filter
      this.filter = {
        deposit_reference: "",
        stock_reference: "",
        section_reference: "",
        item_reference: "",
        startDate: "",
        endDate: "",
      };
    },

    async print() {
      return await this.$store.dispatch("stock/print", {
        section_reference: this.filter.section_reference || "",
        item_reference: this.filter.item_reference || "",
        deposit_reference: this.filter.deposit_reference || "",
        stock_reference: this.filter.stock_reference || "",
      });
    },

    async sendByEmail() {
      return await this.$store.dispatch("stock/sendByEmail");
    },

    onFilter(filterValues) {
      // Update local filter state
      this.filter = { ...this.filter, ...filterValues };
      // Dispatch filter action to store
      this.$store.dispatch("stock/filter", {
        section_reference: this.filter.section_reference || "",
        item_reference: this.filter.item_reference || "",
        deposit_reference: this.filter.deposit_reference || "",
        stock_reference: this.filter.stock_reference || "",
      });
    },

    onReset(resetValues) {
      // Reset filter
      this.filter = {
        deposit_reference: "",
        stock_reference: "",
        section_reference: "",
        item_reference: "",
        startDate: resetValues.startDate || "",
        endDate: resetValues.endDate || "",
      };
      // Reload all stock items
      this.$store.dispatch("stock/fetchStocksItems");
    },
  },
};
</script>
